import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Check,
  ShoppingBag,
  Zap,
} from "lucide-react";

export const KyuStoreWdpPromo32k: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Core Theme Palette (Matching Reference Exactly)
  const bgDark = "#080C0A";
  const neonLime = "#D8FF3E";
  const neonGlow = "rgba(216, 255, 62, 0.45)";
  const magentaPink = "#FF2E93";
  const textWhite = "#FFFFFF";
  const textMuted = "#BAC4BE";
  const cardBorder = "1px solid rgba(255, 255, 255, 0.12)";

  // Helper spring
  const sp = (delay: number, damping = 14, stiffness = 170) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 0.9 },
    });

  // Smooth rotation for concentric radar rings
  const radarRotation = (frame * 0.4) % 360;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgDark,
        color: textWhite,
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* High-Energy Background Audio */}
      <Audio src={staticFile("socl_ref_audio.mp3")} volume={1.0} />

      {/* 1. Subtle Engineering Grid Background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(216, 255, 62, 0.045) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(216, 255, 62, 0.045) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* 2. Top-Right Ambient Radial Glow */}
      <div
        style={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 800,
          height: 800,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(216, 255, 62, 0.14) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      {/* 3. Bottom-Left Secondary Ambient Radial Glow */}
      <div
        style={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(216, 255, 62, 0.09) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      {/* 4. Top-Left Brand Pill [Logo] KYU STORE */}
      <div
        style={{
          position: "absolute",
          top: 70,
          left: 65,
          zIndex: 100,
          display: "inline-flex",
          alignItems: "center",
          gap: 12,
          backgroundColor: "rgba(18, 25, 20, 0.85)",
          padding: "10px 22px",
          borderRadius: 30,
          border: cardBorder,
          backdropFilter: "blur(12px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${neonLime} 0%, #0D1611 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 900, color: "#000000" }}>K</span>
        </div>
        <span
          style={{
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "0.05em",
            color: "#FFFFFF",
          }}
        >
          KYU STORE
        </span>
      </div>

      {/* ========================================================================= */}
      {/* SCENE 1: FRAMES 0 - 100 (0.00s - 3.33s) */}
      {/* "Masih top up in-game?" + Stacked Expensive Cards */}
      {/* ========================================================================= */}
      {frame >= 0 && frame < 100 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 240,
          }}
        >
          {/* Main Headline */}
          <h1
            style={{
              fontSize: 68,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              margin: 0,
              textAlign: "center",
              transform: `translateY(${interpolate(sp(0), [0, 1], [30, 0])}px)`,
              opacity: sp(0),
              textShadow: "0 10px 30px rgba(0,0,0,0.7)",
            }}
          >
            Masih top up in-game?
          </h1>

          {/* Staggered Stacked Notification Cards */}
          <div
            style={{
              position: "relative",
              width: 860,
              marginTop: 65,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "center",
            }}
          >
            {[
              { tag: "50 DM", label: "Top up diamond eceran", price: "-Rp15.000", delay: 4, rot: -2.2 },
              { tag: "WDP", label: "WDP in-game + Pajak PPN", price: "-Rp39.000", delay: 14, rot: 2.0 },
              { tag: "EVENT", label: "Top up dadakan spin skin", price: "-Rp45.000", delay: 24, rot: -1.6 },
              { tag: "MABAR", label: "Beli diamond eceran terus", price: "-Rp50.000", delay: 34, rot: 1.8 },
              { tag: "TOTAL", label: "Beli satuan bikin boncos", price: "-Rp149.000", delay: 44, rot: -0.8 },
            ].map((item, idx) => {
              const cardSp = sp(item.delay, 12, 190);
              const cardY = interpolate(cardSp, [0, 1], [-80, 0]);
              const cardScale = interpolate(cardSp, [0, 1], [0.85, 1]);

              if (frame < item.delay) return null;

              return (
                <div
                  key={idx}
                  style={{
                    width: "100%",
                    height: 98,
                    backgroundColor: "#FFFFFF",
                    color: "#000000",
                    borderRadius: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 30px",
                    boxShadow: "0 14px 34px rgba(0,0,0,0.55)",
                    transform: `translateY(${cardY}px) scale(${cardScale}) rotate(${item.rot}deg)`,
                    transformOrigin: "center center",
                    zIndex: idx + 10,
                  }}
                >
                  {/* Left Badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#0D120F",
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 18,
                          fontWeight: 900,
                          color: neonLime,
                          letterSpacing: "0.02em",
                        }}
                      >
                        {item.tag}
                      </span>
                    </div>

                    {/* Middle Description */}
                    <span
                      style={{
                        fontSize: 27,
                        fontWeight: 700,
                        color: "#18211C",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {item.label}
                    </span>
                  </div>

                  {/* Right Red Deduction */}
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 900,
                      color: "#E63946",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {item.price}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 2: FRAMES 100 - 180 (3.33s - 6.00s) */}
      {/* "Sebulan bisa *habis* ratusan ribu." + "Rp150.000" (Strikethrough in magenta) */}
      {/* ========================================================================= */}
      {frame >= 100 && frame < 180 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 60px",
            textAlign: "center",
          }}
        >
          {/* Headline */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              transform: `scale(${sp(100)})`,
            }}
          >
            <div style={{ fontSize: 64, fontWeight: 800, color: textWhite }}>
              Sebulan bisa{" "}
              <span
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 84,
                  fontWeight: 400,
                  marginLeft: 6,
                }}
              >
                habis
              </span>
            </div>
            <div
              style={{
                fontSize: 88,
                fontWeight: 900,
                color: neonLime,
                textShadow: `0 0 35px ${neonGlow}`,
                lineHeight: 1,
              }}
            >
              ratusan ribu.
            </div>
          </div>

          {/* Strikethrough Price Presentation */}
          <div
            style={{
              marginTop: 70,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              transform: `scale(${sp(108)})`,
            }}
          >
            {/* The Huge Price */}
            <div
              style={{
                fontSize: 118,
                fontWeight: 900,
                fontFamily: "'Anton', sans-serif",
                letterSpacing: "0.02em",
                color: neonLime,
                textShadow: `0 0 45px ${neonGlow}`,
                position: "relative",
                display: "inline-block",
                padding: "0 10px",
              }}
            >
              Rp150.000
              {/* Dynamic Magenta Strikethrough Line */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: "54%",
                  height: 10,
                  backgroundColor: magentaPink,
                  borderRadius: 6,
                  boxShadow: `0 0 20px ${magentaPink}`,
                  width: `${interpolate(frame, [118, 142], [0, 100], {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  })}%`,
                }}
              />
            </div>

            {/* Subtext */}
            <span
              style={{
                marginTop: 18,
                fontSize: 28,
                fontWeight: 600,
                color: textMuted,
                letterSpacing: "0.04em",
              }}
            >
              kalo beli diamond eceran biasa
            </span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 3: FRAMES 180 - 315 (6.00s - 10.50s) */}
      {/* "Di Kyu Store, langganan WDP cuma *32 ribu.*" + Glossy Icon + Radar Rings */}
      {/* ========================================================================= */}
      {frame >= 180 && frame < 315 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {/* Orbital Radar Rings behind the App Icon */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(-50%, -50%) rotate(${radarRotation}deg)`,
              width: 820,
              height: 820,
              pointerEvents: "none",
            }}
          >
            {[520, 680, 820].map((size, idx) => (
              <div
                key={idx}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  border: `1.5px dashed rgba(216, 255, 62, ${0.35 - idx * 0.08})`,
                }}
              />
            ))}
          </div>

          {/* Central Glossy WDP Icon */}
          <div
            style={{
              transform: `scale(${sp(180)})`,
              width: 170,
              height: 170,
              borderRadius: 44,
              background: "linear-gradient(145deg, #1A241E 0%, #0C120F 100%)",
              border: `2px solid ${neonLime}`,
              boxShadow: `0 0 50px rgba(216, 255, 62, 0.4), 0 20px 45px rgba(0,0,0,0.85)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              zIndex: 10,
            }}
          >
            {/* Top-Right Specular Glass Reflection */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 90,
                height: 90,
                borderRadius: "0 42px 0 90px",
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(216, 255, 62, 0) 100%)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                fontSize: 38,
                fontWeight: 900,
                color: "#FFFFFF",
                lineHeight: 1,
              }}
            >
              KYU
            </span>
            <span
              style={{
                fontSize: 38,
                fontWeight: 900,
                color: neonLime,
                lineHeight: 1,
                marginTop: 4,
              }}
            >
              WDP
            </span>
          </div>

          {/* Product Subtitle */}
          <div
            style={{
              marginTop: 34,
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 34,
              fontWeight: 800,
              transform: `scale(${sp(190)})`,
              zIndex: 10,
            }}
          >
            <span>Weekly Diamond Pass</span>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 44,
                color: neonLime,
              }}
            >
              Official
            </span>
          </div>

          {/* Headline */}
          <div
            style={{
              marginTop: 26,
              fontSize: 54,
              fontWeight: 800,
              transform: `translateY(${interpolate(sp(200), [0, 1], [30, 0])}px)`,
              opacity: sp(200),
              zIndex: 10,
            }}
          >
            Di Kyu Store, WDP cuma{" "}
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 72,
                color: neonLime,
              }}
            >
              32 ribu.
            </span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 4: FRAMES 315 - 395 (10.50s - 13.17s) */}
      {/* "Total 220 Diamond. Cuan maksimal." + Kyu Store Window Mockup */}
      {/* ========================================================================= */}
      {frame >= 315 && frame < 395 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 180,
          }}
        >
          {/* Headline */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 58,
              fontWeight: 800,
              transform: `translateY(${interpolate(sp(315), [0, 1], [30, 0])}px)`,
              opacity: sp(315),
            }}
          >
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 74,
              }}
            >
              Total
            </span>
            <span>220 DM.</span>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 74,
                marginLeft: 14,
              }}
            >
              Cuan
            </span>
            <span>maksimal.</span>
          </div>

          {/* Desktop macOS Window Mockup */}
          <div
            style={{
              width: 950,
              height: 980,
              marginTop: 38,
              backgroundColor: "#101612",
              borderRadius: 24,
              border: "2px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 35px 70px rgba(0,0,0,0.9)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transform: `scale(${sp(320)})`,
            }}
          >
            {/* Window Title Header */}
            <div
              style={{
                height: 52,
                backgroundColor: "#17211B",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 22px",
              }}
            >
              {/* Traffic light dots */}
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <span style={{ fontSize: 18, fontWeight: 700, color: textMuted }}>
                Kyu Store — Weekly Diamond Pass Instant System
              </span>
              <div style={{ width: 40 }} />
            </div>

            {/* URL Input Bar & Action */}
            <div
              style={{
                height: 60,
                backgroundColor: "#0D1410",
                borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                display: "flex",
                alignItems: "center",
                padding: "0 20px",
                gap: 14,
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 800, color: neonLime }}>
                KYUSTORE®
              </span>
              <div
                style={{
                  flex: 1,
                  height: 38,
                  backgroundColor: "#16201A",
                  borderRadius: 10,
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 14px",
                  fontSize: 15,
                  color: "#9EA9A2",
                }}
              >
                https://kyustore.com/weekly-pass
              </div>
              <div
                style={{
                  backgroundColor: neonLime,
                  color: "#000000",
                  padding: "8px 18px",
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 900,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Zap size={16} color="#000000" />
                <span>Scan QRIS</span>
              </div>
            </div>

            {/* Main Center Display (WDP Breakdown) */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
              {/* Left Details Card */}
              <div
                style={{
                  flex: 1.2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#080C0A",
                  padding: 24,
                  gap: 20,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 20,
                    backgroundColor: "#131C16",
                    border: `2px solid ${neonLime}`,
                    boxShadow: `0 0 35px ${neonGlow}`,
                    padding: "30px 28px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 24, fontWeight: 900, color: neonLime }}>
                      WEEKLY PASS BENEFIT
                    </span>
                    <span
                      style={{
                        backgroundColor: neonLime,
                        color: "#000000",
                        padding: "6px 14px",
                        borderRadius: 8,
                        fontSize: 15,
                        fontWeight: 900,
                      }}
                    >
                      SUPER CUAN
                    </span>
                  </div>

                  <div style={{ textAlign: "center", margin: "16px 0" }}>
                    <div style={{ fontSize: 72, fontWeight: 900, fontFamily: "'Anton', sans-serif", color: "#FFFFFF" }}>
                      220 DIAMOND
                    </div>
                    <div style={{ fontSize: 20, color: textMuted, fontWeight: 700 }}>
                      Klaim harian 80 DM instan + 20 DM x 7 Hari
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: "#0B100C",
                      borderRadius: 14,
                      padding: "16px 20px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px dashed rgba(255,255,255,0.15)",
                    }}
                  >
                    <span style={{ fontSize: 18, color: textWhite, fontWeight: 700 }}>
                      Harga Resmi Kyu Store:
                    </span>
                    <span style={{ fontSize: 28, fontWeight: 900, color: neonLime }}>
                      Rp32.000
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Panel: Package Selection */}
              <div
                style={{
                  flex: 0.8,
                  borderLeft: "1px solid rgba(255, 255, 255, 0.08)",
                  padding: "24px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  backgroundColor: "#0E1511",
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 900, color: neonLime }}>
                  PILIHAN BUNDLE PASS
                </span>

                {[
                  { name: "1x Weekly Pass", dm: "220 DM", price: "Rp32.000", active: true },
                  { name: "2x Weekly Pass", dm: "440 DM", price: "Rp64.000", active: false },
                  { name: "3x Weekly Pass", dm: "660 DM", price: "Rp96.000", active: false },
                ].map((pkg, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "16px 14px",
                      borderRadius: 14,
                      backgroundColor: pkg.active ? "rgba(216, 255, 62, 0.15)" : "#151F19",
                      border: pkg.active ? `2px solid ${neonLime}` : "1px solid rgba(255,255,255,0.08)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: pkg.active ? neonLime : textWhite }}>
                        {pkg.name}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 800, color: textMuted }}>{pkg.dm}</span>
                    </div>
                    <span style={{ fontSize: 20, fontWeight: 900, color: pkg.active ? neonLime : textWhite }}>
                      {pkg.price}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Status Panel */}
            <div
              style={{
                height: 72,
                backgroundColor: "#0D1310",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                padding: "0 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: neonLime }} />
                <span style={{ fontSize: 15, fontWeight: 700, color: textWhite }}>
                  Server Otomatis 24 Jam — Detik Itu Juga Masuk Akun
                </span>
              </div>
              <span style={{ fontSize: 15, fontWeight: 800, color: neonLime }}>
                100% AMAN & LEGAL
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 5: FRAMES 395 - 475 (13.17s - 15.83s) */}
      {/* "Kata anak mabar:" + Telegram/WA Chat Testimonial */}
      {/* ========================================================================= */}
      {frame >= 395 && frame < 475 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 240,
          }}
        >
          {/* Headline */}
          <h1
            style={{
              fontSize: 66,
              fontWeight: 800,
              margin: 0,
              transform: `translateY(${interpolate(sp(395), [0, 1], [30, 0])}px)`,
              opacity: sp(395),
            }}
          >
            Kata anak mabar:
          </h1>

          {/* Telegram Chat Window Card */}
          <div
            style={{
              width: 900,
              marginTop: 50,
              backgroundColor: "#141C17",
              borderRadius: 28,
              border: cardBorder,
              padding: "36px 34px",
              boxShadow: "0 30px 65px rgba(0,0,0,0.85)",
              display: "flex",
              flexDirection: "column",
              gap: 24,
              transform: `scale(${sp(400)})`,
            }}
          >
            {/* Chat 1: Rian */}
            <div style={{ display: "flex", gap: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#8B5CF6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#FFFFFF",
                }}
              >
                R
              </div>
              <div style={{ flex: 1, backgroundColor: "#1B2620", borderRadius: 16, padding: "14px 20px" }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#C4B5FD" }}>Rian</span>
                <p style={{ margin: "6px 0 0", fontSize: 22, color: "#FFFFFF", fontWeight: 600 }}>
                  wdp di kyu store beneran cuma 32k min?
                </p>
              </div>
            </div>

            {/* Chat 2: Fajar ML */}
            <div style={{ display: "flex", gap: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#06B6D4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: 900,
                  color: "#FFFFFF",
                }}
              >
                F
              </div>
              <div style={{ flex: 1, backgroundColor: "#1B2620", borderRadius: 16, padding: "14px 20px" }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#67E8F9" }}>Fajar</span>
                <p style={{ margin: "6px 0 0", fontSize: 22, color: "#FFFFFF", fontWeight: 600 }}>
                  bayar pake qris langsung landing ke akun kah?
                </p>
              </div>
            </div>

            {/* Chat 3: Dimas (The Highlighted Testimonial) */}
            <div
              style={{
                display: "flex",
                gap: 16,
                transform: `scale(${sp(412)})`,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#F43F5E",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#FFFFFF",
                }}
              >
                D
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: "#202E26",
                  borderRadius: 18,
                  padding: "16px 22px",
                  border: `2px solid ${neonLime}`,
                  boxShadow: `0 0 25px rgba(216, 255, 62, 0.25)`,
                }}
              >
                {/* Quoted Message Preview */}
                <div
                  style={{
                    borderLeft: "3px solid #8B5CF6",
                    paddingLeft: 12,
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#C4B5FD" }}>Rian</span>
                  <div style={{ fontSize: 16, color: textMuted }}>wdp di kyu store beneran cuma 32k min?</div>
                </div>

                <span style={{ fontSize: 20, fontWeight: 900, color: "#FDA4AF" }}>Dimas Gendong</span>
                <p style={{ margin: "6px 0 0", fontSize: 25, color: "#FFFFFF", fontWeight: 800 }}>
                  Asli worth it bgt! Dapet 220 DM cuma 32k, di in-game kena 39k ada pajak!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCENE 6: FRAMES 475 - 684 (15.83s - 22.80s) */}
      {/* "Cuma Rp32 ribu. *Cuan Parah.*" + Rp32.000 + 3 Checks + CTA Button */}
      {/* ========================================================================= */}
      {frame >= 475 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 180,
            paddingBottom: 80,
            justifyContent: "space-between",
          }}
        >
          {/* Top Hook Text */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `scale(${sp(475)})`,
            }}
          >
            <div style={{ fontSize: 62, fontWeight: 800 }}>
              Cuma <span style={{ color: neonLime }}>Rp32 ribu.</span>
            </div>
            <div
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 66,
                color: "#FFFFFF",
                marginTop: -4,
              }}
            >
              Cuan Parah.
            </div>
          </div>

          {/* Central Price & App Squircle Box */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `scale(${sp(482)})`,
            }}
          >
            {/* Small Glossy Icon */}
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 26,
                background: "linear-gradient(145deg, #1A241E 0%, #0C120F 100%)",
                border: `2px solid ${neonLime}`,
                boxShadow: `0 0 35px rgba(216, 255, 62, 0.4)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <span style={{ fontSize: 24, fontWeight: 900, color: "#FFFFFF", lineHeight: 1 }}>KYU</span>
              <span style={{ fontSize: 24, fontWeight: 900, color: neonLime, lineHeight: 1, marginTop: 2 }}>WDP</span>
            </div>

            {/* Massive Price */}
            <div
              style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 124,
                lineHeight: 0.95,
                color: "#FFFFFF",
                letterSpacing: "0.02em",
                textShadow: `0 0 45px ${neonGlow}`,
              }}
            >
              Rp32.000
            </div>

            {/* Pill Badge: WEEKLY DIAMOND PASS • PROMO */}
            <div
              style={{
                marginTop: 20,
                backgroundColor: neonLime,
                color: "#000000",
                padding: "12px 34px",
                borderRadius: 30,
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.06em",
                boxShadow: "0 6px 20px rgba(216, 255, 62, 0.4)",
              }}
            >
              WEEKLY DIAMOND PASS • PROMO
            </div>
          </div>

          {/* 3 Checklist Features */}
          <div
            style={{
              width: 780,
              display: "flex",
              flexDirection: "column",
              gap: 22,
              transform: `scale(${sp(490)})`,
            }}
          >
            {[
              "Total 220+ Diamond Langsung Masuk",
              "Proses Otomatis Detik Itu Juga",
              "100% Legal & Aman Anti-Minus",
            ].map((feat, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#FFFFFF",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: "rgba(216, 255, 62, 0.15)",
                    border: `2px solid ${neonLime}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Check size={26} color={neonLime} strokeWidth={3.5} />
                </div>
                <span>{feat}</span>
              </div>
            ))}
          </div>

          {/* Bottom Store CTA Button */}
          <div
            style={{
              width: 860,
              height: 94,
              backgroundColor: neonLime,
              borderRadius: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              boxShadow: `0 14px 40px rgba(216, 255, 62, 0.45)`,
              transform: `scale(${sp(500)})`,
            }}
          >
            <ShoppingBag size={38} color="#000000" strokeWidth={2.4} />
            <span
              style={{
                fontSize: 36,
                fontWeight: 900,
                color: "#000000",
                letterSpacing: "-0.01em",
              }}
            >
              ORDER DI KYUSTORE.COM
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
